import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { X } from "lucide-react"
import type { TagOption } from "@/types"

type Props = {
  popularTags: TagOption[]
  selectedTagIds: number[]
  customTags: string[]
  onTagIdsChange: (ids: number[]) => void
  onCustomTagsChange: (tags: string[]) => void
}

export function TagSelector({
  popularTags,
  selectedTagIds,
  customTags,
  onTagIdsChange,
  onCustomTagsChange,
}: Props) {
  const [tagInput, setTagInput] = useState("")

  const handleTagToggle = (tagId: number) => {
    const newIds = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    onTagIdsChange(newIds)
  }

  const handleAddCustomTag = () => {
    const tag = tagInput.trim().toLowerCase()
    if (tag && !customTags.includes(tag)) {
      onCustomTagsChange([...customTags, tag])
      setTagInput("")
    }
  }

  const handleRemoveCustomTag = (tag: string) => {
    onCustomTagsChange(customTags.filter((t) => t !== tag))
  }

  return (
    <div>
      <Label>Tags</Label>

      {popularTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {popularTags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id)
            return (
              <Button
                key={tag.id}
                type="button"
                variant={isSelected ? "default" : "outline"}
                size="sm"
                className="text-xs"
                onClick={() => handleTagToggle(tag.id)}
              >
                {tag.name}
              </Button>
            )
          })}
        </div>
      )}

      {/* Custom Tags Input */}
      <div className="mt-3">
        <div className="flex gap-2">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Add custom tag..."
            className="flex-1 text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                handleAddCustomTag()
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleAddCustomTag}
            disabled={!tagInput.trim()}
          >
            Add
          </Button>
        </div>

        {customTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {customTags.map((tag) => (
              <Badge key={tag} variant="secondary" className="gap-1">
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveCustomTag(tag)}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
